import {
  execOnLoad,
  getA11yTranslations,
  formatPrice,
  initDiscountAppPricing,
  requestDiscountAppPrices,
} from '@general/helpers';
import Swiper from 'swiper';
import { A11y, EffectFade, Navigation, Pagination } from 'swiper/modules';
import 'swiper/scss';
import 'swiper/scss/navigation';
import breakpoints from 'breakpoints';
import { throttle } from 'lodash';
import { initProductForm } from '@components/product';
import { openMinicart } from '@components/minicart';
import { getWishlist, addToWishlist, deleteFromWishlist, updateWishlistBtn } from '@components/wishlist';
import '../../snippets/quick-buy/quick-buy.js';

const BASE_CLASS = 'product-tile__container';
const INITIALIZED_CLASS = `${BASE_CLASS}--initialized`;

function resetFormOptions(form) {
  if (!form) return;
  form
    .querySelectorAll(
      '.quick-buy__option input[type="radio"]:checked, .product-tile__quick-buy-wrapper input[type="radio"]:checked',
    )
    .forEach((input) => {
      input.checked = false;
    });
  const idInput = form.querySelector('input[name="id"]');
  if (idInput && !form.classList.contains('product-tile__form--single-variant')) {
    idInput.value = '';
  }
}

function handleQuickBuy(snippetContainer) {
  const quickBuy = snippetContainer.querySelector('.product-tile__quick-buy');
  const quickBuyWrapper = snippetContainer.querySelector('.product-tile__quick-buy-wrapper');
  const quickBuyClose = snippetContainer.querySelector('.quick-buy__close');
  const form = snippetContainer.querySelector('.product-tile__form');
  const submit = snippetContainer.querySelector('.quick-buy__add-to-cart');
  const error = snippetContainer.querySelector('.quick-buy__error');
  const wishlistButton = snippetContainer.querySelector('[data-wishlist-btn]');
  if (!quickBuy || !form) return;

  resetFormOptions(form);

  quickBuyClose?.addEventListener('click', () => {
    setTimeout(() => {
      quickBuy.classList.remove('hidden');
      resetFormOptions(form);
    }, 400);
    error.classList.add('hidden');
    quickBuyWrapper.classList.remove('active');
    submit.disabled = false;
  });

  submit?.addEventListener('click', (e) => {
    const formData = new FormData(form);
    if (!formData.get('id')) {
      e.preventDefault();
      quickBuyWrapper.classList.add('active');
      setTimeout(() => {
        quickBuy.classList.add('hidden');
      }, 400);
    }
  });

  initProductForm({
    form,
    callbacks: {
      afterVariantChange: async ({ variant, product }) => {
        if (wishlistButton) {
          wishlistButton.dataset.wishlistVariantId = variant?.id;
          wishlistButton.dataset.wishlistPrice = formatPrice({ value: variant?.price ?? 0 });
          wishlistButton.dataset.wishlistCompareAtPrice = variant?.compare_at_price
            ? formatPrice({ value: variant?.compare_at_price })
            : '';
          const wishlist = await getWishlist();
          const wishlistProduct = wishlist?.listcontents?.find(
            (addedProduct) => addedProduct.epi == variant.id && addedProduct.empi == product.id,
          );
          wishlistButton.dataset.wishlistStatus = wishlistProduct ? 'active' : 'inactive';
        }
        if (submit && !submit.disabled) {
          error.classList.add('hidden');
          submit.click();
        } else {
          error.classList.remove('hidden');
        }
      },
      beforeSubmit: () => {
        const uniqueIdInput = form.querySelector('.js-unique-id');
        if (uniqueIdInput) uniqueIdInput.value = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      },
      afterSubmit: () => {
        quickBuyClose?.click();
        openMinicart(true);
      },
    },
  });
}

if (!window.dAgency) window.dAgency = {};

// Initialize quick-buy listener to show quick-buy modal
if (!window.dAgency.quickBuyListenerInitialized) {
  document.addEventListener('click', async (e) => {
    const quickBuyBtn = e.target.closest('.product-tile__quick-buy--modal-btn');
    if (!quickBuyBtn) return;

    const handle = quickBuyBtn.dataset.handle;
    if (!handle) return;

    const modalWrapper = document.getElementById('quick-view-modal');
    if (!modalWrapper) return;

    quickBuyBtn.classList.add('loading');

    // Fetch product quick-buy HTML via the sr-quick-buy section (`?sections=sr-quick-buy`)
    const root = window.Shopify?.routes?.root || '/';
    try {
      const res = await fetch(`${root}products/${handle}?sections=sr-quick-buy`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      const html = data['sr-quick-buy'];
      if (!html) throw new Error('No sr-quick-buy section in response');

      const wrapper = document.createElement('div');
      wrapper.innerHTML = html;

      const content = wrapper.firstElementChild;
      if (!content) throw new Error('No content found in section');

      modalWrapper.replaceChildren(content);

      const modal = modalWrapper.querySelector('.quick-buy__container');
      if (modal && typeof window?.dAgency?.initQuickBuy === 'function') {
        window.dAgency.initQuickBuy(modal);
      }
    } catch (err) {
      console.error('Error loading quick-buy modal', err);
    } finally {
      quickBuyBtn.classList.remove('loading');
    }
  });

  // Loaded only once
  window.dAgency.quickBuyListenerInitialized = true;
}

/**
 * @param {HTMLElement} context - The tile, or an element wrapping or inside it.
 * @param {Object} [options]
 * @param {boolean} [options.inInitialMarkup=false] - The tile was in the page
 *   Discount Depot scanned on load, so the app requests its discount itself.
 */
window.dAgency.initProductTile = (context, { inInitialMarkup = false } = {}) => {
  /**
   * Container resolution with sequential fallback strategy
   * Supports multiple context types from static templates to dynamic Section Rendering API
   *
   * Search priority:
   * 1. Direct match - fastest (static product tiles)
   * 2. Parent traversal - template wrappers
   * 3. Child query - dynamic sections (recently viewed carousel)
   */
  let snippetContainer = null;

  // 1. Direct match - fastest (static product tiles)
  snippetContainer = context.classList.contains(BASE_CLASS) ? context : null;

  // 2. Parent traversal - template wrappers
  if (!snippetContainer) snippetContainer = context.closest(`.${BASE_CLASS}`);

  // 3. Child query - dynamic sections (recently viewed carousel)
  if (!snippetContainer) snippetContainer = context.querySelector(`.${BASE_CLASS}`);

  if (!snippetContainer || snippetContainer.classList.contains(INITIALIZED_CLASS)) return;

  // Tag transformations
  const tagMappings = {
    productTitleTag: '.product-tile__title',
    productVendorTag: '.product-tile__vendor',
  };
  Object.entries(tagMappings).forEach(([datasetKey, selector]) => {
    const tagValue = snippetContainer.dataset[datasetKey];
    if (!tagValue) return;

    const element = snippetContainer.querySelector(selector);
    if (!element || tagValue === element.tagName.toLowerCase()) return;

    const newElement = document.createElement(tagValue);
    newElement.className = element.className;
    newElement.innerHTML = element.innerHTML;
    element.parentNode.replaceChild(newElement, element);

    // Remove processed dataset attribute
    delete snippetContainer.dataset[datasetKey];
  });

  const swiperWrapper = snippetContainer.querySelector('.product-tile__gallery > .swiper-wrapper');
  if (swiperWrapper && swiperWrapper.querySelectorAll('.swiper-slide').length > 1) {
    const swiperContainer = swiperWrapper.closest('.product-tile__gallery');
    const pagination = swiperContainer.querySelector('.product-tile__gallery-pagination');
    const options = {
      modules: [
        ...(window.dAgency.translations ? [A11y] : []),
        EffectFade,
        Navigation,
        ...(pagination ? [Pagination] : []),
      ],
      crossFade: true,
      effect: 'fade',
      navigation: {
        prevEl: swiperContainer.querySelector('.product-tile__gallery-arrow--left'),
        nextEl: swiperContainer.querySelector('.product-tile__gallery-arrow--right'),
      },
      ...(pagination
        ? {
            pagination: {
              el: pagination,
            },
          }
        : {}),
      ...getA11yTranslations(),
    };
    var autoChange = true;
    var changeEnter = false;
    var changeLeave = false;
    const swiper = new Swiper(swiperContainer, options);
    swiperContainer.addEventListener('mouseenter', () => {
      if (window.innerWidth <= parseInt(breakpoints.lg) || !autoChange) return;
      changeEnter = true;
      swiper.slideTo(1);
    });

    swiperContainer.addEventListener('mouseleave', () => {
      if (window.innerWidth <= parseInt(breakpoints.lg) || !autoChange) return;
      changeLeave = true;
      swiper.slideTo(0);
    });

    swiper.on('slideChangeTransitionStart', () => {
      if (!changeEnter && !changeLeave) {
        autoChange = false;
        return;
      }
      if (changeEnter) changeEnter = false;
      if (changeLeave) changeLeave = false;
    });
  }

  const wishlistButton = snippetContainer.querySelector('[data-wishlist-btn]');

  if (wishlistButton) {
    updateWishlistBtn(wishlistButton);

    const toggleLoading = throttle(() => {
      wishlistButton.classList.toggle('loading');
    }, 500);

    wishlistButton.addEventListener('click', async (e) => {
      e.preventDefault();
      toggleLoading();

      const variantID = wishlistButton?.dataset?.wishlistVariantId;
      const masterID = wishlistButton?.dataset?.wishlistProductId;
      const productURL = wishlistButton?.dataset?.wishlistProductUrl;
      const price = wishlistButton?.dataset?.wishlistPrice;
      const compareAtPrice = wishlistButton?.dataset?.wishlistCompareAtPrice;

      if (wishlistButton?.dataset?.wishlistStatus === 'active') {
        await deleteFromWishlist({ variantID, masterID, productURL, button: wishlistButton });
      } else {
        await addToWishlist({
          variantID,
          masterID,
          productURL,
          compareAtPrice,
          price,
          button: wishlistButton,
        });
      }
      toggleLoading();
    });
  }

  handleQuickBuy(snippetContainer);
  initDiscountAppPricing(snippetContainer);
  if (!inInitialMarkup) requestDiscountAppPrices(snippetContainer);
  snippetContainer.classList.add(INITIALIZED_CLASS);
};

execOnLoad((tile) => window.dAgency.initProductTile(tile, { inInitialMarkup: true }), '.product-tile__container');
